import { useState, useRef, useCallback, useEffect } from 'react';
import { X, ChevronUp, ChevronDown, Plus } from 'lucide-react';
import NumberLineElement from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/NumberLineElement';
import ImageElement from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/ImageElement';
import ExpressionNumeriqueElement from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/ExpressionNumeriqueElement';
import GeogebraElement from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/GeogebraElement';

// ─── Marker format ───
const MARKER_RE = /\{\{element:(\d+)\}\}/g;

// Parse texte + elements into segments
// Garantit toujours un segment html entre deux éléments (pour pouvoir taper du texte)
const parseSegments = (texte, elements) => {
  if (!texte) return [{ type: 'html', content: '', id: 1 }];

  const raw = [];
  let lastIndex = 0;
  let match;
  let idCounter = 1;
  const re = new RegExp(MARKER_RE.source, 'g');

  while ((match = re.exec(texte)) !== null) {
    const before = texte.slice(lastIndex, match.index);
    if (before || raw.length === 0) {
      raw.push({ type: 'html', content: before, id: idCounter++ });
    }
    const elIdx = parseInt(match[1], 10);
    if (elements && elements[elIdx]) {
      raw.push({ ...elements[elIdx], id: idCounter++, _elIdx: elIdx });
    }
    lastIndex = re.lastIndex;
  }
  const remaining = texte.slice(lastIndex);
  raw.push({ type: 'html', content: remaining, id: idCounter++ });

  // Garantir un segment html entre chaque paire d'éléments et aux extrémités
  const parts = [];
  for (let i = 0; i < raw.length; i++) {
    const seg = raw[i];
    if (seg.type !== 'html') {
      // Si le précédent n'est pas html, insérer un html vide
      if (parts.length === 0 || parts[parts.length - 1].type !== 'html') {
        parts.push({ type: 'html', content: '', id: idCounter++ });
      }
    }
    parts.push(seg);
  }
  // Toujours terminer par un segment html
  if (parts.length === 0 || parts[parts.length - 1].type !== 'html') {
    parts.push({ type: 'html', content: '', id: idCounter++ });
  }

  return parts;
};

// Serialize segments back to texte + elements
// Les segments html vides (espaces éditeur) sont nettoyés s'ils ne contiennent rien de visible
const isHtmlEmpty = (html) => {
  if (!html) return true;
  const stripped = html.replace(/<br\s*\/?>/gi, '').replace(/&nbsp;/gi, '').replace(/<[^>]*>/g, '').trim();
  return stripped.length === 0;
};

const serializeSegments = (segments) => {
  let texte = '';
  const elements = [];
  segments.forEach((seg) => {
    if (seg.type === 'html') {
      // Ne pas inclure les html vides dans le texte final
      if (!isHtmlEmpty(seg.content)) {
        texte += seg.content;
      }
    } else {
      const idx = elements.length;
      texte += `{{element:${idx}}}`;
      elements.push({ type: seg.type, config: seg.config || {} });
    }
  });
  return { texte, elements };
};

// ─── Couleurs ───
const COLORS = [
  { value: '#0F172A', label: 'Défaut' },
  { value: '#1E3A8A', label: 'Bleu' },
  { value: '#0891B2', label: 'Cyan' },
  { value: '#10B981', label: 'Vert' },
  { value: '#EF4444', label: 'Rouge' },
  { value: '#F59E0B', label: 'Orange' },
  { value: '#8B5CF6', label: 'Violet' },
];

// ─── Types d'éléments insérables ───
const ELEMENT_TYPES = [
  { type: 'image', icon: '🖼', label: 'Image' },
  { type: 'expression', icon: 'ƒ', label: 'Math' },
  { type: 'numberline', icon: '📏', label: 'Droite' },
  { type: 'geogebra', icon: '△', label: 'GeoGebra' },
];

// ─── Bouton toolbar ───
const TBtn = ({ icon, label, active, onClick, disabled, small }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={label}
    className={`flex items-center justify-center rounded transition-all flex-shrink-0 ${
      small ? 'w-[26px]' : 'w-[30px]'
    } h-7 text-xs font-medium ${
      active
        ? 'bg-sky-50 text-sky-700 outline outline-1 outline-sky-200'
        : disabled
          ? 'text-gray-300 cursor-default'
          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
    }`}
  >
    {icon}
  </button>
);

const Sep = () => <div className="w-px h-[18px] bg-gray-200 mx-0.5 flex-shrink-0" />;

// ─── Color Picker ───
const ColorPicker = ({ onSelect, currentColor }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-flex">
      <TBtn
        icon={
          <span className="flex flex-col items-center gap-px">
            <span className="text-xs leading-none font-bold">A</span>
            <span className="w-3 h-[3px] rounded-sm" style={{ background: currentColor || '#0F172A' }} />
          </span>
        }
        label="Couleur du texte"
        active={open}
        onClick={() => setOpen(!open)}
      />
      {open && (
        <>
          <div className="fixed inset-0 z-[99]" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen(false)} />
          <div className="absolute top-8 left-1/2 -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-lg p-1.5 flex gap-1 z-[100]">
            {COLORS.map(c => (
              <div
                key={c.value}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onSelect(c.value); setOpen(false); }}
                title={c.label}
                className="w-[22px] h-[22px] rounded cursor-pointer transition-all hover:scale-110"
                style={{
                  background: c.value,
                  border: currentColor === c.value ? '2px solid #fff' : '2px solid transparent',
                  boxShadow: currentColor === c.value ? `0 0 0 1.5px ${c.value}` : '0 0 0 1px #e2e8f0',
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Table Insert Grid ───
const TableInsert = ({ onInsert }) => {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState({ r: 0, c: 0 });
  const maxR = 5, maxC = 5;

  return (
    <div className="relative inline-flex">
      <TBtn icon="▦" label="Insérer un tableau" active={open} onClick={() => setOpen(!open)} />
      {open && (
        <>
          <div className="fixed inset-0 z-[99]" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen(false)} />
          <div className="absolute top-8 left-1/2 -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-[100]">
            <p className="text-[10px] text-gray-400 text-center font-semibold mb-1.5">
              {hover.r > 0 ? `${hover.r} × ${hover.c}` : 'Taille du tableau'}
            </p>
            <div className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${maxC}, 18px)` }}>
              {Array.from({ length: maxR * maxC }).map((_, i) => {
                const r = Math.floor(i / maxC) + 1;
                const c = (i % maxC) + 1;
                const isActive = r <= hover.r && c <= hover.c;
                return (
                  <div
                    key={i}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setHover({ r, c })}
                    onClick={() => { onInsert(r, c); setOpen(false); setHover({ r: 0, c: 0 }); }}
                    className={`w-[18px] h-[18px] rounded-sm border-[1.5px] cursor-pointer transition-all ${
                      isActive ? 'border-sky-400 bg-sky-50' : 'border-gray-200 bg-gray-50'
                    }`}
                  />
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Element Insert Dropdown ───
const ElementInsert = ({ onInsert }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-flex">
      <TBtn icon="+" label="Insérer un élément" active={open} onClick={() => setOpen(!open)} />
      {open && (
        <>
          <div className="fixed inset-0 z-[99]" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen(false)} />
          <div className="absolute top-8 right-0 bg-white border border-gray-200 rounded-lg shadow-lg p-1.5 z-[100] flex gap-1">
            {ELEMENT_TYPES.map(et => (
              <button
                key={et.type}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onInsert(et.type); setOpen(false); }}
                title={et.label}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-gray-600 hover:bg-sky-50 hover:text-sky-700 transition whitespace-nowrap"
              >
                <span className="text-sm">{et.icon}</span>
                {et.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Bottom Insert Button ───
const BottomInsert = ({ onInsert }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center justify-center py-2 border-t border-gray-100">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-7 h-7 rounded-full border-2 border-dashed border-gray-300 text-gray-400 flex items-center justify-center hover:border-sky-400 hover:text-sky-500 hover:bg-sky-50 transition-all"
        >
          <Plus size={14} strokeWidth={2.5} />
        </button>
      ) : (
        <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-50 border border-gray-200">
          {ELEMENT_TYPES.map(et => (
            <button
              key={et.type}
              type="button"
              onClick={() => { onInsert(et.type); setOpen(false); }}
              title={et.label}
              className="flex items-center gap-1 px-2 py-1.5 rounded-md text-xs text-gray-600 hover:bg-sky-50 hover:text-sky-700 transition whitespace-nowrap"
            >
              <span className="text-sm">{et.icon}</span>
              {et.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition ml-1"
          >
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Single contentEditable text segment ───
const TextSegment = ({ content, onChange, onKeyUp, onClick, placeholder }) => {
  const ref = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (ref.current && !initializedRef.current && content) {
      ref.current.innerHTML = content;
      initializedRef.current = true;
    }
  }, []);

  const handleInput = useCallback(() => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    const text = ref.current.textContent?.trim();
    onChange(text ? html : '');
  }, [onChange]);

  const isEmpty = !content;

  return (
    <div className="relative">
      {isEmpty && placeholder && (
        <div className="absolute top-3 left-3.5 text-gray-400 text-sm pointer-events-none">
          {placeholder}
        </div>
      )}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyUp={onKeyUp}
        onClick={onClick}
        onPaste={(e) => {
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
        }}
        className="rich-text-editor min-h-[32px] px-3.5 py-2.5 text-sm text-gray-700 leading-relaxed outline-none"
      />
    </div>
  );
};

// ─── Inline Element Block ───
const InlineElement = ({ segment, onConfigChange, onRemove, canMoveUp, canMoveDown, onMoveUp, onMoveDown }) => {
  const renderElement = () => {
    switch (segment.type) {
      case 'image':
        return <ImageElement config={segment.config} onConfigChange={onConfigChange} onRemove={onRemove} />;
      case 'expression':
        return <ExpressionNumeriqueElement config={segment.config} onConfigChange={onConfigChange} onRemove={onRemove} />;
      case 'numberline':
        return <NumberLineElement config={segment.config} onConfigChange={onConfigChange} onRemove={onRemove} />;
      case 'geogebra':
        return <GeogebraElement config={segment.config} onConfigChange={onConfigChange} onRemove={onRemove} />;
      default:
        return null;
    }
  };

  const meta = ELEMENT_TYPES.find(e => e.type === segment.type);

  return (
    <div className="relative group my-1">
      {/* Badge type + contrôles */}
      <div className="flex items-center gap-1 mb-1">
        <span className="text-[10px] text-gray-400 flex items-center gap-1">
          <span>{meta?.icon}</span>
          {meta?.label}
        </span>
        {/* Flèches réordonnement + supprimer — visibles au hover */}
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={!canMoveUp}
            className="w-4 h-4 rounded flex items-center justify-center text-gray-400 hover:text-sky-600 hover:bg-sky-50 disabled:opacity-25 disabled:cursor-not-allowed transition"
            title="Monter"
          >
            <ChevronUp size={11} />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={!canMoveDown}
            className="w-4 h-4 rounded flex items-center justify-center text-gray-400 hover:text-sky-600 hover:bg-sky-50 disabled:opacity-25 disabled:cursor-not-allowed transition"
            title="Descendre"
          >
            <ChevronDown size={11} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="w-4 h-4 rounded flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition"
            title="Supprimer"
          >
            <X size={10} />
          </button>
        </div>
      </div>
      {renderElement()}
    </div>
  );
};

// ─── Main RichTextBlock ───
const RichTextBlock = ({ texte, elements, onChange }) => {
  // Parse into segments
  const [segments, setSegments] = useState(() => parseSegments(texte, elements));
  const [activeFormats, setActiveFormats] = useState({});
  const [textColor, setTextColor] = useState('#0F172A');

  // Emit changes
  const emitChange = useCallback((newSegments) => {
    const result = serializeSegments(newSegments);
    onChange(result);
  }, [onChange]);

  // Update a text segment's content
  const updateTextContent = useCallback((segId, html) => {
    setSegments(prev => {
      const next = prev.map(s => s.id === segId ? { ...s, content: html } : s);
      emitChange(next);
      return next;
    });
  }, [emitChange]);

  // Update an element's config
  const updateElementConfig = useCallback((segId, newConfig) => {
    setSegments(prev => {
      const next = prev.map(s => s.id === segId ? { ...s, config: newConfig } : s);
      emitChange(next);
      return next;
    });
  }, [emitChange]);

  // Remove an element segment (merge surrounding text)
  const removeElement = useCallback((segId) => {
    setSegments(prev => {
      const idx = prev.findIndex(s => s.id === segId);
      if (idx === -1) return prev;
      const next = [...prev];
      next.splice(idx, 1);
      // Merge consecutive html segments
      const merged = [];
      for (const seg of next) {
        if (seg.type === 'html' && merged.length > 0 && merged[merged.length - 1].type === 'html') {
          merged[merged.length - 1] = {
            ...merged[merged.length - 1],
            content: (merged[merged.length - 1].content || '') + (seg.content || ''),
          };
        } else {
          merged.push(seg);
        }
      }
      if (merged.length === 0) merged.push({ type: 'html', content: '', id: Date.now() });
      emitChange(merged);
      return merged;
    });
  }, [emitChange]);

  // Insert element at end (or we could do at cursor, but for now append)
  const insertElement = useCallback((type) => {
    setSegments(prev => {
      const newId = Date.now();
      const next = [
        ...prev,
        { type, config: {}, id: newId },
        { type: 'html', content: '', id: newId + 1 },
      ];
      emitChange(next);
      return next;
    });
  }, [emitChange]);

  // Move an element up or down (swap with adjacent element, text stays in place)
  const moveElement = useCallback((segId, direction) => {
    setSegments(prev => {
      // Collect indices of element segments
      const elementIndices = prev.reduce((acc, seg, i) => {
        if (seg.type !== 'html') acc.push(i);
        return acc;
      }, []);

      const segIdx = prev.findIndex(s => s.id === segId);
      const elPos = elementIndices.indexOf(segIdx);
      const targetElPos = elPos + direction;
      if (targetElPos < 0 || targetElPos >= elementIndices.length) return prev;

      const targetIdx = elementIndices[targetElPos];
      const next = [...prev];
      // Swap the two element segments
      [next[segIdx], next[targetIdx]] = [next[targetIdx], next[segIdx]];
      emitChange(next);
      return next;
    });
  }, [emitChange]);

  // Format tracking
  const updateActiveFormats = useCallback(() => {
    const block = document.queryCommandValue('formatBlock');
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList: document.queryCommandState('insertOrderedList'),
      justifyLeft: document.queryCommandState('justifyLeft'),
      justifyCenter: document.queryCommandState('justifyCenter'),
      justifyRight: document.queryCommandState('justifyRight'),
      h3: block === 'h3',
      h4: block === 'h4',
    });
  }, []);

  // Ref vers le container des segments pour forcer le focus
  const segmentsContainerRef = useRef(null);

  // Forcer le focus sur le dernier contentEditable si aucun n'est focus
  const ensureFocus = useCallback(() => {
    const active = document.activeElement;
    if (active?.contentEditable === 'true') return; // déjà focus dans un éditeur
    const editables = segmentsContainerRef.current?.querySelectorAll('[contenteditable="true"]');
    if (editables?.length > 0) {
      const last = editables[editables.length - 1];
      last.focus();
      // Placer le curseur à la fin
      const range = document.createRange();
      range.selectNodeContents(last);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, []);

  const exec = useCallback((cmd, val = null) => {
    ensureFocus();
    document.execCommand(cmd, false, val);
    updateActiveFormats();
  }, [updateActiveFormats, ensureFocus]);

  const setHeading = useCallback((tag) => {
    const current = document.queryCommandValue('formatBlock');
    exec('formatBlock', current === tag ? 'p' : tag);
  }, [exec]);

  const applyColor = useCallback((color) => {
    setTextColor(color);
    exec('foreColor', color);
  }, [exec]);

  const insertTable = useCallback((rows, cols) => {
    ensureFocus();
    let html = '<table>';
    for (let r = 0; r < rows; r++) {
      html += '<tr>';
      for (let c = 0; c < cols; c++) {
        html += r === 0 ? '<th>&nbsp;</th>' : '<td>&nbsp;</td>';
      }
      html += '</tr>';
    }
    html += '</table><p><br></p>';
    document.execCommand('insertHTML', false, html);
  }, [ensureFocus]);

  // Sticky toolbar detection
  const sentinelRef = useRef(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Check if first text segment is empty (for placeholder)
  const firstTextEmpty = segments.length > 0 && segments[0].type === 'html' && !segments[0].content;
  const isOnlyOneEmptyText = segments.length === 1 && firstTextEmpty;

  return (
    <div className="border border-gray-200 rounded-lg bg-white transition-all focus-within:border-sky-300">
      {/* Sentinelle pour détecter le sticky */}
      <div ref={sentinelRef} className="h-0" />
      {/* ─── Toolbar ─── */}
      <div className={`flex items-center gap-px px-1.5 py-1 border-b border-gray-100 flex-wrap min-h-[36px] sticky top-0 z-10 rounded-t-lg transition-shadow ${
        isStuck ? 'bg-white shadow-md' : 'bg-gray-50/80'
      }`}>
        <TBtn icon={<span className="text-[11px] font-extrabold">H3</span>} label="Titre" active={activeFormats.h3} onClick={() => setHeading('h3')} small />
        <TBtn icon={<span className="text-[10px] font-bold">H4</span>} label="Sous-titre" active={activeFormats.h4} onClick={() => setHeading('h4')} small />
        <Sep />
        <TBtn icon={<b>G</b>} label="Gras (Ctrl+B)" active={activeFormats.bold} onClick={() => exec('bold')} />
        <TBtn icon={<i className="italic">I</i>} label="Italique (Ctrl+I)" active={activeFormats.italic} onClick={() => exec('italic')} />
        <TBtn icon={<u>S</u>} label="Souligné (Ctrl+U)" active={activeFormats.underline} onClick={() => exec('underline')} />
        <TBtn icon={<s className="line-through">ab</s>} label="Barré" active={activeFormats.strikeThrough} onClick={() => exec('strikeThrough')} />
        <Sep />
        <ColorPicker currentColor={textColor} onSelect={applyColor} />
        <Sep />
        <TBtn icon="•≡" label="Liste à puces" active={activeFormats.insertUnorderedList} onClick={() => exec('insertUnorderedList')} />
        <TBtn icon="1≡" label="Liste numérotée" active={activeFormats.insertOrderedList} onClick={() => exec('insertOrderedList')} />
        <Sep />
        <TBtn icon="≡←" label="Aligner à gauche" active={activeFormats.justifyLeft} onClick={() => exec('justifyLeft')} small />
        <TBtn icon="≡↔" label="Centrer" active={activeFormats.justifyCenter} onClick={() => exec('justifyCenter')} small />
        <TBtn icon="≡→" label="Aligner à droite" active={activeFormats.justifyRight} onClick={() => exec('justifyRight')} small />
        <Sep />
        <TableInsert onInsert={insertTable} />
        <Sep />
        <ElementInsert onInsert={insertElement} />
      </div>

      {/* ─── Segments ─── */}
      <div ref={segmentsContainerRef}>
        {segments.map((seg, idx) => {
          if (seg.type === 'html') {
            return (
              <TextSegment
                key={seg.id}
                content={seg.content}
                onChange={(html) => updateTextContent(seg.id, html)}
                onKeyUp={updateActiveFormats}
                onClick={updateActiveFormats}
                placeholder={idx === 0 && isOnlyOneEmptyText ? "Écris l'énoncé de ta question..." : null}
              />
            );
          }

          // Calcul position parmi les éléments (pour flèches haut/bas)
          const elementIds = segments.filter(s => s.type !== 'html').map(s => s.id);
          const elPos = elementIds.indexOf(seg.id);

          return (
            <div key={seg.id} className="px-3 py-1">
              <InlineElement
                segment={seg}
                onConfigChange={(cfg) => updateElementConfig(seg.id, cfg)}
                onRemove={() => removeElement(seg.id)}
                canMoveUp={elPos > 0}
                canMoveDown={elPos < elementIds.length - 1}
                onMoveUp={() => moveElement(seg.id, -1)}
                onMoveDown={() => moveElement(seg.id, 1)}
              />
            </div>
          );
        })}
      </div>

      {/* ─── Bouton + en bas ─── */}
      <BottomInsert onInsert={insertElement} />
    </div>
  );
};

export default RichTextBlock;
